// -------------------------------------------------------------
//  KPICard  – Executive metric tile with trend + sparkline
// -------------------------------------------------------------
import React from "react";
import { LineChart, Line, ResponsiveContainer } from "recharts";

const TREND_UP   = "#2d8c4e";
const TREND_DOWN = "#c0392b";
const NEUTRAL    = "#4969ab";

export default function KPICard({
  title, value, target, unit = "", trend = 0,
  trendData = [], prefix = "", suffix = "",
  icon = "??", color = NEUTRAL, size = "md",
}) {
  const isPositive = trend >= 0;
  const trendColor = trend > 0 ? TREND_UP : trend < 0 ? TREND_DOWN : NEUTRAL;
  const widths     = { sm: 180, md: 220, lg: 280 };
  const w = widths[size] || 220;

  const pct = target ? Math.min(100, Math.round((parseFloat(value) / parseFloat(target)) * 100)) : null;

  return (
    <div style={{
      width: w, minHeight: 130,
      background: "#fff",
      borderRadius: 12,
      boxShadow: "0 2px 12px rgba(73,105,171,0.10)",
      padding: "16px 18px",
      borderLeft: `4px solid ${color}`,
      display: "flex", flexDirection: "column", gap: 4,
      position: "relative", overflow: "hidden",
    }}>
      {/* bg watermark icon */}
      <span style={{
        position: "absolute", right: 10, top: 8,
        fontSize: 38, opacity: 0.07, userSelect: "none",
      }}>{icon}</span>

      <div style={{ fontSize: 11, color: "#888", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.8 }}>
        {title}
      </div>

      <div style={{ fontSize: 26, fontWeight: 800, color: "#1a2340", lineHeight: 1.1 }}>
        {prefix}{typeof value === "number" ? value.toLocaleString("en-IN") : value}{suffix}
        <span style={{ fontSize: 12, color: "#888", fontWeight: 400, marginLeft: 4 }}>{unit}</span>
      </div>

      {target && (
        <div style={{ fontSize: 11, color: "#aaa" }}>
          Target: {prefix}{typeof target === "number" ? target.toLocaleString("en-IN") : target}{suffix} {unit}
        </div>
      )}

      {/* Progress bar */}
      {pct !== null && (
        <div style={{ marginTop: 4 }}>
          <div style={{ background: "#f0f2f8", borderRadius: 4, height: 5, width: "100%" }}>
            <div style={{
              width: `${pct}%`, height: 5, borderRadius: 4,
              background: pct >= 90 ? TREND_UP : pct >= 70 ? "#e67e22" : TREND_DOWN,
              transition: "width 0.6s ease",
            }} />
          </div>
          <div style={{ fontSize: 10, color: "#aaa", marginTop: 2 }}>{pct}% of target</div>
        </div>
      )}

      {/* Trend badge */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}>
        <span style={{
          fontSize: 11, fontWeight: 700, color: trendColor,
          background: `${trendColor}18`, borderRadius: 20,
          padding: "2px 8px",
        }}>
          {isPositive ? "+" : "-"} {Math.abs(trend)}%
        </span>
        <span style={{ fontSize: 10, color: "#bbb" }}>vs last period</span>
      </div>

      {/* Sparkline */}
      {trendData.length > 1 && (
        <div style={{ marginTop: 6, opacity: 0.6, width: "100%", height: 28 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={trendData.map((v, i) => ({ v, i }))}>
              <Line
                type="monotone"
                dataKey="v"
                stroke={color}
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
